/**
 * ErrorDisplay Component
 *
 * Unified error presentation component that parses error types
 * and shows user-friendly messages with optional technical details.
 *
 * @module components/ui/ErrorDisplay
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle, AlertTriangle, Info, RefreshCw, LogIn, ChevronDown, ChevronUp, X } from 'lucide-react';
import { formatError, ERROR_TYPES } from '../../utils/errorMessages';

/**
 * Icon mapping for error severities
 */
const severityIcons = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

/**
 * Color classes for error severities
 */
const severityStyles = {
  error: {
    container: 'bg-error-500/20 border-error-500/30',
    icon: 'text-error-400',
    title: 'text-error-300',
    message: 'text-error-300/90',
    button: 'bg-error-500/20 hover:bg-error-500/30 border-error-500/30 text-error-300',
  },
  warning: {
    container: 'bg-warning-500/20 border-warning-500/30',
    icon: 'text-warning-400',
    title: 'text-warning-300',
    message: 'text-warning-300/90',
    button: 'bg-warning-500/20 hover:bg-warning-500/30 border-warning-500/30 text-warning-300',
  },
  info: {
    container: 'bg-secondary-500/20 border-secondary-500/30',
    icon: 'text-secondary-400',
    title: 'text-secondary-300',
    message: 'text-secondary-300/90',
    button: 'bg-secondary-500/20 hover:bg-secondary-500/30 border-secondary-500/30 text-secondary-300',
  },
};

/**
 * ErrorDisplay - Unified error presentation component
 *
 * @param {Object} props - Component props
 * @param {Error|Object|string} props.error - Error to display
 * @param {Function} props.onRetry - Callback for retry action
 * @param {Function} props.onDismiss - Callback for dismiss action
 * @param {Function} props.onReconnect - Callback for reconnect action (auth errors)
 * @param {boolean} props.showTechnical - Show technical details by default
 * @param {boolean} props.collapsible - Allow collapsing technical details
 * @param {boolean} props.dismissible - Show dismiss button
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} props.inline - Inline display mode (minimal padding)
 */
const ErrorDisplay = ({
  error,
  onRetry,
  onDismiss,
  onReconnect,
  showTechnical = false,
  collapsible = true,
  dismissible = false,
  className = '',
  size = 'md',
  inline = false,
}) => {
  const [showDetails, setShowDetails] = useState(showTechnical);

  if (!error) return null;

  // Format the error
  const formatted = formatError(error, { showTechnical: true });
  const Icon = severityIcons[formatted.severity] || AlertCircle;
  const styles = severityStyles[formatted.severity] || severityStyles.error;

  // Size classes
  const sizeClasses = {
    sm: {
      container: 'p-3',
      icon: 'h-4 w-4',
      title: 'text-sm',
      message: 'text-xs',
      button: 'px-2 py-1 text-xs',
    },
    md: {
      container: 'p-4',
      icon: 'h-5 w-5',
      title: 'text-sm font-medium',
      message: 'text-sm',
      button: 'px-3 py-1.5 text-sm',
    },
    lg: {
      container: 'p-6',
      icon: 'h-6 w-6',
      title: 'text-base font-medium',
      message: 'text-base',
      button: 'px-4 py-2 text-sm',
    },
  };

  const sizes = sizeClasses[size];

  // Determine which action button to show
  const showRetry = onRetry && formatted.action === 'retry';
  const showReconnect = onReconnect && formatted.action === 'reconnect';
  const hasActions = showRetry || showReconnect || dismissible;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        ${styles.container} border rounded-lg
        ${inline ? 'py-2 px-3' : sizes.container}
        ${className}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <Icon
          className={`${sizes.icon} ${styles.icon} flex-shrink-0 mt-0.5`}
          aria-hidden="true"
        />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className={`${sizes.title} ${styles.title}`}>
            {formatted.title}
          </p>

          {/* Message */}
          <p className={`${sizes.message} ${styles.message} mt-1`}>
            {formatted.message}
          </p>

          {/* Technical Details */}
          {formatted.technical && collapsible && formatted.technical !== formatted.message && (
            <div className="mt-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className={`flex items-center gap-1 ${sizes.message} text-gray-400 hover:text-gray-300 transition-colors`}
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    Show details
                  </>
                )}
              </button>
              {showDetails && (
                <pre className="mt-2 p-2 bg-black/20 rounded text-xs text-gray-400 overflow-x-auto">
                  {formatted.technical}
                </pre>
              )}
            </div>
          )}

          {/* Actions */}
          {hasActions && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {showRetry && (
                <button
                  onClick={onRetry}
                  className={`${sizes.button} ${styles.button} rounded-lg border flex items-center gap-1.5 transition-colors`}
                >
                  <RefreshCw className="h-3 w-3" />
                  Try Again
                </button>
              )}
              {showReconnect && (
                <button
                  onClick={onReconnect}
                  className={`${sizes.button} ${styles.button} rounded-lg border flex items-center gap-1.5 transition-colors`}
                >
                  <LogIn className="h-3 w-3" />
                  Reconnect
                </button>
              )}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={`${styles.icon} hover:opacity-75 transition-opacity flex-shrink-0`}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

ErrorDisplay.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.instanceOf(Error),
    PropTypes.object,
    PropTypes.string,
  ]),
  onRetry: PropTypes.func,
  onDismiss: PropTypes.func,
  onReconnect: PropTypes.func,
  showTechnical: PropTypes.bool,
  collapsible: PropTypes.bool,
  dismissible: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  inline: PropTypes.bool,
};

/**
 * InlineError - Compact inline error display
 */
export const InlineError = ({ error, onRetry, className = '' }) => (
  <ErrorDisplay
    error={error}
    onRetry={onRetry}
    inline
    size="sm"
    collapsible={false}
    className={className}
  />
);

InlineError.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.instanceOf(Error),
    PropTypes.object,
    PropTypes.string,
  ]),
  onRetry: PropTypes.func,
  className: PropTypes.string,
};

/**
 * ErrorBanner - Full-width error banner for page-level errors
 */
export const ErrorBanner = ({
  error,
  onRetry,
  onDismiss,
  onReconnect,
  className = '',
}) => (
  <ErrorDisplay
    error={error}
    onRetry={onRetry}
    onDismiss={onDismiss}
    onReconnect={onReconnect}
    dismissible={!!onDismiss}
    size="md"
    className={`rounded-none border-x-0 ${className}`}
  />
);

ErrorBanner.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.instanceOf(Error),
    PropTypes.object,
    PropTypes.string,
  ]),
  onRetry: PropTypes.func,
  onDismiss: PropTypes.func,
  onReconnect: PropTypes.func,
  className: PropTypes.string,
};

/**
 * RateLimitError - Specialized display for rate limit errors
 */
export const RateLimitError = ({
  resetTime,
  onWait,
  className = '',
}) => (
  <ErrorDisplay
    error={{
      type: ERROR_TYPES.RATE_LIMIT,
      message: resetTime
        ? `GitHub API rate limit reached. Resets in ${resetTime}.`
        : 'GitHub API rate limit reached. Please wait before retrying.',
    }}
    onRetry={onWait}
    size="md"
    className={className}
  />
);

RateLimitError.propTypes = {
  resetTime: PropTypes.string,
  onWait: PropTypes.func,
  className: PropTypes.string,
};

/**
 * ConnectionError - Specialized display for connection errors
 */
export const ConnectionError = ({
  onRetry,
  onReconnect,
  className = '',
}) => (
  <ErrorDisplay
    error={{ type: ERROR_TYPES.NETWORK }}
    onRetry={onRetry}
    onReconnect={onReconnect}
    size="md"
    className={className}
  />
);

ConnectionError.propTypes = {
  onRetry: PropTypes.func,
  onReconnect: PropTypes.func,
  className: PropTypes.string,
};

export default ErrorDisplay;
